import React, { useState, useEffect, useCallback } from 'react';

const API = '/api';

async function api(endpoint, method = 'GET', body = null, token = null) {
  const h = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  const r = await fetch(`${API}${endpoint}`, { method, headers: h, body: body ? JSON.stringify(body) : null });
  return r.json();
}

const THEMES = { gold: '#f59e0b', blue: '#3b82f6', green: '#10b981', purple: '#8b5cf6', red: '#ef4444' };

export default function App() {
  const [lang, setLang] = useState(() => localStorage.getItem('tb_lang') || 'ar');
  const [theme, setTheme] = useState(() => localStorage.getItem('tb_theme') || 'gold');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('tb_dark') !== 'false');
  const [page, setPage] = useState(() => localStorage.getItem('tb_token') ? 'loading' : 'landing');
  const [token, setToken] = useState(() => localStorage.getItem('tb_token') || '');
  const [user, setUser] = useState(null);
  const [activePage, setActivePage] = useState('signals');
  const [authMode, setAuthMode] = useState('login');
  const [siteSettings, setSiteSettings] = React.useState({});
  React.useEffect(() => { api('/settings/public2').then(r => { if(r && !r.error) setSiteSettings(r); }); }, []);
  const [authErr, setAuthErr] = useState('');
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [uname, setUname] = useState('');
  const [toast, setToast] = useState(null);

  const accent = THEMES[theme] || THEMES.gold;
  const bg = darkMode ? '#0a0f1e' : '#f0f4f8';
  const card = darkMode ? '#0f172a' : '#ffffff';
  const text = darkMode ? '#e2e8f0' : '#1e293b';
  const muted = darkMode ? '#475569' : '#94a3b8';
  const border = darkMode ? '#1e293b' : '#e2e8f0';

  useEffect(() => { localStorage.setItem('tb_lang', lang); }, [lang]);
  useEffect(() => { localStorage.setItem('tb_theme', theme); }, [theme]);
  useEffect(() => { localStorage.setItem('tb_dark', darkMode); }, [darkMode]);

  useEffect(() => {
    if (token) {
      api('/auth/me', 'GET', null, token).then(r => {
        if (r && !r.error) { setUser(r); setPage('app'); }
        else { localStorage.removeItem('tb_token'); setToken(''); }
      });
    }
  }, [token]);

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const [showDisclaimer, setShowDisclaimer] = React.useState(false);
  const [pendingRegData, setPendingRegData] = React.useState(null);
  const [disclaimerChecked, setDisclaimerChecked] = React.useState(false);
  if (page === "loading") return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh"}}>...</div>;

  const handleAuth = async () => {
    setAuthErr('');
    try {
      if (authMode === 'register') {
        setPendingRegData({ name: uname, email, password: pass });
        setShowDisclaimer(true);
        setDisclaimerChecked(false);
        return;
      }
      const ep = '/auth/login';
      const body = { email, password: pass };
      const r = await api(ep, 'POST', body);
      if (r.error) { setAuthErr(r.error); return; }
      localStorage.setItem('tb_token', r.token);
      setToken(r.token);
      setUser(r.user);
      setPage('app');
    } catch (e) {
      setAuthErr('خطأ في الاتصال بالسيرفر');
    }
  };

  const completeRegister = async () => {
    try {
      const r = await api('/auth/register', 'POST', { ...pendingRegData, disclaimer_accepted: true });
      if (r.error) { setAuthErr(r.error); setShowDisclaimer(false); return; }
      localStorage.setItem('tb_token', r.token);
      setToken(r.token);
      setUser(r.user);
      setPage('app');
      setShowDisclaimer(false);
    } catch (e) {
      setAuthErr('خطأ في الاتصال بالسيرفر');
    }
  };

  const logout = () => {
    localStorage.removeItem('tb_token');
    localStorage.removeItem('tb_alpaca');
    setToken('');
    setUser(null);
    setPage('landing');
  };

  const T = lang === 'ar' ? {
    dir: 'rtl', signals: 'الإشارات', portfolio: 'المحفظة', orders: 'الأوامر',
    bot: 'Auto Bot', alpaca: 'ربط Alpaca', alerts: 'التنبيهات', sharia: 'فحص الشرعية',
    support: 'الدعم الفني', ai: 'تحليل الذكاء الاصطناعي', theme: 'إعدادات المظهر',
    plans: 'الخطط', admin: 'الإدارة', logout: 'خروج', login: 'تسجيل الدخول',
    register: 'إنشاء حساب', email: 'البريد الإلكتروني', password: 'كلمة المرور',
    name: 'الاسم', buy: 'شراء', sell: 'بيع', free: 'مجاني', pro: 'برو', elite: 'إيليت',
  } : {
    dir: 'ltr', signals: 'Signals', portfolio: 'Portfolio', orders: 'Orders',
    bot: 'Auto Bot', alpaca: 'Alpaca', alerts: 'Alerts', sharia: 'Sharia Check',
    support: 'Support', ai: 'AI Analysis', theme: 'Theme', plans: 'Plans',
    admin: 'Admin', logout: 'Logout', login: 'Login', register: 'Register',
    email: 'Email', password: 'Password', name: 'Name', buy: 'Buy', sell: 'Sell',
    free: 'Free', pro: 'Pro', elite: 'Elite',
  };

  const css = `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Cairo', 'Segoe UI', sans-serif; }
    .card { background: ${card}; border: 1px solid ${border}; border-radius: 14px; padding: 20px; }
    .btn { padding: 10px 20px; border-radius: 8px; border: none; cursor: pointer; font-family: inherit; font-weight: 700; font-size: 14px; transition: opacity 0.2s; }
    .btn:hover { opacity: 0.85; }
    .btn-gold { background: ${accent}; color: #000; }
    .btn-outline { background: transparent; border: 1px solid ${accent}; color: ${accent}; }
    .inp { width: 100%; padding: 10px 14px; border-radius: 8px; border: 1px solid ${border}; background: ${bg}; color: ${text}; font-family: inherit; font-size: 14px; outline: none; }
    .inp:focus { border-color: ${accent}; }
    .fade { animation: fadeIn 0.3s ease; }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    .sidebar { width: 220px; min-height: 100vh; background: ${card}; border-${T.dir === 'rtl' ? 'left' : 'right'}: 1px solid ${border}; display: flex; flex-direction: column; padding: 16px 0; }
    .sidebar-item { display: flex; align-items: center; gap: 10px; padding: 10px 16px; cursor: pointer; color: ${muted}; font-size: 14px; transition: all 0.2s; border-radius: 8px; margin: 2px 8px; }
    .sidebar-item:hover { background: ${bg}; color: ${text}; }
    .sidebar-item.active { background: ${accent}22; color: ${accent}; font-weight: 700; }
    .main { flex: 1; padding: 24px; overflow-y: auto; }
    .stat-card { background: ${card}; border: 1px solid ${border}; border-radius: 12px; padding: 16px; text-align: center; }
    @media (max-width: 768px) { .sidebar { width: 60px; } .sidebar-item span:last-child { display: none; } .main { padding: 16px; } }
  `;

  if (page === 'landing') return <Landing accent={accent} bg={bg} card={card} text={text} muted={muted} border={border} css={css} T={T} darkMode={darkMode} setPage={setPage} setAuthMode={setAuthMode} />;
  if (showDisclaimer) return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#1a1a2e', borderRadius: 16, padding: 28, maxWidth: 500, width: '100%', border: '1px solid #2a2a4a' }}>
        <h2 style={{ color: '#f59e0b', marginBottom: 16, textAlign: 'center' }}>⚠️ إقرار المسؤولية</h2>
        <div style={{ background: '#0d0d1a', borderRadius: 8, padding: 16, marginBottom: 16, maxHeight: 250, overflowY: 'auto', color: '#9ca3af', fontSize: 13, lineHeight: 1.8 }}>
          {siteSettings.disclaimer || `هذا الموقع يقدم تحليلات وإشارات تداول للأغراض التعليمية فقط وليس نصيحة مالية. التداول في الأسواق المالية ينطوي على مخاطر عالية وقد تخسر جزءاً أو كل رأس مالك. يجب عليك الاستعانة بمستشار مالي مرخص قبل اتخاذ أي قرارات استثمارية. الموقع وإدارته غير مسؤولين عن أي خسائر مالية تنتج عن استخدام هذه الخدمة.`}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, cursor: 'pointer' }} onClick={() => setDisclaimerChecked(!disclaimerChecked)}>
          <div style={{ width: 20, height: 20, borderRadius: 4, border: '2px solid #f59e0b', background: disclaimerChecked ? '#f59e0b' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {disclaimerChecked && <span style={{ color: '#000', fontSize: 12, fontWeight: 900 }}>✓</span>}
          </div>
          <span style={{ color: '#fff', fontSize: 13 }}>أقر بأنني قرأت وفهمت وأوافق على الشروط أعلاه</span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowDisclaimer(false)} style={{ flex: 1, padding: 12, borderRadius: 8, border: '1px solid #2a2a4a', background: 'transparent', color: '#aaa', cursor: 'pointer' }}>إلغاء</button>
          <button onClick={completeRegister} disabled={!disclaimerChecked} style={{ flex: 2, padding: 12, borderRadius: 8, border: 'none', background: disclaimerChecked ? '#f59e0b' : '#333', color: disclaimerChecked ? '#000' : '#666', fontWeight: 700, cursor: disclaimerChecked ? 'pointer' : 'not-allowed' }}>
            إنشاء الحساب
          </button>
        </div>
      </div>
    </div>
  );

  if (page === 'auth') return <Auth accent={accent} bg={bg} card={card} text={text} muted={muted} border={border} css={css} T={T} authMode={authMode} setAuthMode={setAuthMode} email={email} setEmail={setEmail} pass={pass} setPass={setPass} uname={uname} setUname={setUname} authErr={authErr} handleAuth={handleAuth} setPage={setPage} />;

  return (
    <div style={{ minHeight: '100vh', background: bg, display: 'flex', direction: T.dir }}>
      <style>{css}</style>
      {toast && <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', background: toast.type === 'err' ? '#2d0a0a' : '#052e16', border: `1px solid ${toast.type === 'err' ? '#ef4444' : '#10b981'}`, borderRadius: 10, padding: '12px 20px', color: toast.type === 'err' ? '#ef4444' : '#10b981', fontWeight: 700, zIndex: 1000 }}>{toast.msg}</div>}

      <Sidebar T={T} user={user} activePage={activePage} setActivePage={setActivePage} accent={accent} logout={logout} lang={lang} setLang={setLang} />
      <main className="main">
        {siteSettings.announcement && (
          <div style={{ background: accent, color: '#fff', fontSize: 13, padding: '6px 0', overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ display: 'inline-block', whiteSpace: 'nowrap', animation: 'marquee 20s linear infinite' }}>
              📢 {siteSettings.announcement} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; 📢 {siteSettings.announcement}
            </div>
            <style>{`@keyframes marquee { from { transform: translateX(100vw); } to { transform: translateX(-100%); } }`}</style>
          </div>
        )}
        <PageRouter activePage={activePage} token={token} user={user} accent={accent} bg={bg} card={card} text={text} muted={muted} border={border} T={T} showToast={showToast} setActivePage={setActivePage} lang={lang} theme={theme} setTheme={setTheme} darkMode={darkMode} setDarkMode={setDarkMode} />
      </main>
    </div>
  );
}

function Landing({ accent, bg, card, text, muted, border, css, T, darkMode, setPage, setAuthMode }) {
  return (
    <>
    <div style={{ minHeight: '100vh', background: bg, direction: T.dir }}>
      <style>{css}</style>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 24px', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: card, border: `1px solid ${border}`, borderRadius: 100, padding: '6px 16px', fontSize: 13, color: accent, marginBottom: 32 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#34d399', display: 'inline-block' }} />
          يعمل الآن · ١٢٠٠+ متداول نشط
        </div>
        <h1 style={{ fontSize: 'clamp(36px,6vw,72px)', fontWeight: 900, color: text, lineHeight: 1.1, marginBottom: 24 }}>
          تداول آلي<br />
          <span style={{ background: `linear-gradient(90deg,${accent},#ef4444)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>بذكاء اصطناعي</span>
        </h1>
        <p style={{ fontSize: 18, color: muted, maxWidth: 500, margin: '0 auto 40px', lineHeight: 1.8 }}>
          إشارات دقيقة · ربط مباشر مع Alpaca · تنفيذ تلقائي
        </p>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-gold" style={{ fontSize: 17, padding: '14px 40px' }} onClick={() => { setAuthMode('register'); setPage('auth'); }}>ابدأ مجاناً ←</button>
          <button className="btn btn-outline" style={{ padding: '14px 32px' }} onClick={() => { setAuthMode('login'); setPage('auth'); }}>تسجيل الدخول</button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 48, marginTop: 64, flexWrap: 'wrap' }}>
          {[['$2.4M+', 'أرباح المستخدمين'], ['73%', 'نسبة الفوز'], ['١٢٠٠+', 'متداول نشط'], ['24/7', 'مراقبة مستمرة']].map(([v, l]) => (
            <div key={l} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 900, color: accent }}>{v}</div>
              <div style={{ fontSize: 13, color: muted, marginTop: 4 }}>{l}</div>
            </div>
          ))}
        </div>
    {/* أزرار التواصل العائمة - لاندينج */}
    <div style={{ position: 'fixed', bottom: 80, right: 20, display: 'flex', flexDirection: 'column', gap: 10, zIndex: 9999 }}>
      <a href="https://wa.me/966500830905" target="_blank" rel="noreferrer"
        style={{ width: 50, height: 50, borderRadius: '50%', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', textDecoration: 'none', fontSize: 24 }}
        title="واتساب">💬</a>
      <a href="https://t.me/Bshaar_Nayef" target="_blank" rel="noreferrer"
        style={{ width: 50, height: 50, borderRadius: '50%', background: '#229ED9', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', textDecoration: 'none', fontSize: 24 }}
        title="تلغرام">✈️</a>
    </div>
      </div>
    </div>
    </>
  );
}

function Auth({ accent, bg, card, text, muted, border, css, T, authMode, setAuthMode, email, setEmail, pass, setPass, uname, setUname, authErr, handleAuth, setPage }) {

  return (
    <div style={{ minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', direction: T.dir }}>
      <style>{css}</style>

      <div className="card fade" style={{ width: '100%', maxWidth: 400, padding: 32 }}>
        <h2 style={{ fontSize: 22, fontWeight: 900, color: text, marginBottom: 24, textAlign: 'center' }}>
          {authMode === 'login' ? T.login : T.register}
        </h2>
        {authMode === 'register' && <div style={{ marginBottom: 16 }}><label style={{ fontSize: 12, color: muted, display: 'block', marginBottom: 6 }}>{T.name}</label><input className="inp" value={uname} onChange={e => setUname(e.target.value)} placeholder="اسمك" /></div>}
        <div style={{ marginBottom: 16 }}><label style={{ fontSize: 12, color: muted, display: 'block', marginBottom: 6 }}>{T.email}</label><input className="inp" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="example@email.com" style={{ direction: 'ltr' }} /></div>
        <div style={{ marginBottom: 20 }}><label style={{ fontSize: 12, color: muted, display: 'block', marginBottom: 6 }}>{T.password}</label><input className="inp" type="password" value={pass} onChange={e => setPass(e.target.value)} placeholder="••••••••" /></div>
        {authErr && <div style={{ background: '#2d0a0a', border: '1px solid #ef4444', borderRadius: 8, padding: '10px 14px', color: '#ef4444', fontSize: 13, marginBottom: 16 }}>{authErr}</div>}
        <button className="btn btn-gold" style={{ width: '100%', padding: 13, fontSize: 15 }} onClick={handleAuth}>{authMode === 'login' ? T.login : T.register}←</button>
        <p style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: muted }}>
          {authMode === 'login' ? 'ليس لديك حساب؟ ' : 'لديك حساب؟ '}
          <span style={{ color: accent, cursor: 'pointer' }} onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}>
            {authMode === 'login' ? T.register : T.login}
          </span>
        </p>
        {authMode === 'login' && (
          <p style={{ textAlign: 'center', marginTop: 8, fontSize: 13, color: muted }}>
            <span style={{ color: accent, cursor: 'pointer' }} onClick={() => setAuthMode('forgot')}>
              نسيت كلمة السر؟
            </span>
          </p>
        )}
        {authMode === 'forgot' && (
          <div style={{ marginTop: 8 }}>
            <p style={{ textAlign: 'center', fontSize: 13, color: muted, marginBottom: 12 }}>أدخل بريدك الإلكتروني وسنرسل لك كلمة مرور جديدة</p>
            <button className="btn btn-gold" style={{ width: '100%', padding: 13, fontSize: 15 }} onClick={async () => {
              const r = await api('/auth/forgot-password', 'POST', { email });
              if (r && r.message) { setAuthErr(r.message); setAuthMode('login'); }
              else setAuthErr(r?.error || 'خطأ');
            }}>إرسال كلمة المرور</button>
          </div>
        )}
        <p style={{ textAlign: 'center', marginTop: 8, fontSize: 12, color: muted, cursor: 'pointer' }} onClick={() => setPage('landing')}>← العودة للرئيسية</p>
      </div>
    </div>
  );
}

function Sidebar({ T, user, activePage, setActivePage, accent, logout, lang, setLang }) {
  const navItems = [
    ['📊', T.signals, 'signals'],
    ['💼', T.portfolio, 'portfolio'],
    ['📋', T.orders, 'orders'],
    ['🤖', T.bot, 'bot'],
    ['🔗', T.alpaca, 'alpaca'],
    ['🔔', T.alerts, 'alerts'],
    ['🕌', T.sharia, 'sharia'],
    ['🎫', T.support, 'support'],
    ['🧠', T.ai, 'ai'],
    ['⚙️', T.theme, 'theme'],
    ['💎', T.plans, 'plans'],
    ...(user?.is_admin ? [['🛡️', T.admin, 'admin']] : []),
    ['📅', lang === 'ar' ? 'التقرير' : 'Report', 'report'],
    ['👤', lang === 'ar' ? 'حسابي' : 'Profile', 'profile'],
  ['📊', lang === 'ar' ? 'مراحل وينشتاين' : 'Weinstein', 'weinstein'],
  ['🎯', lang === 'ar' ? 'إشارات الأوبشن' : 'Options Signals', 'options'],
  ];

  return (
    <aside className="sidebar">
      <div style={{ padding: '0 16px 16px', borderBottom: `1px solid #1e293b` }}>
        <div style={{ fontWeight: 900, fontSize: 16, color: accent }}>TradeBot</div>
        {user && <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>{user.name}</div>}
        {user && <div style={{ fontSize: 11, background: accent + '22', color: accent, borderRadius: 100, padding: '2px 8px', display: 'inline-block', marginTop: 4, textTransform: 'uppercase' }}>{user.plan}</div>}
      </div>
      <nav style={{ flex: 1, marginTop: 8 }}>
        {navItems.map(([icon, label, id]) => (
          <div key={id} className={`sidebar-item${activePage === id ? ' active' : ''}`} onClick={() => setActivePage(id)}>
            <span style={{ fontSize: 16 }}>{icon}</span>
            <span>{label}</span>
          </div>
        ))}
      </nav>
      <div style={{ padding: '8px' }}>
        <div className="sidebar-item" onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}>
          <span>🌐</span><span>{lang === 'ar' ? 'EN' : 'AR'}</span>
        </div>
        <div className="sidebar-item" onClick={logout} style={{ color: '#ef4444' }}>
          <span>🚪</span><span>{T.logout}</span>
        </div>
      </div>
    </aside>
  );
}

function PageRouter({ activePage, ...props }) {
  const pages = { signals: SignalsPage, portfolio: PortfolioPage, orders: OrdersPage, bot: BotPage, alpaca: AlpacaPage, alerts: AlertsPage, sharia: ShariaPage, support: SupportPage, ai: AIPage, theme: ThemePage, plans: PlansPage, admin: AdminPage, profile: ProfilePage, report: ReportPage, weinstein: WeinsteinPage, options: OptionsSignalsPage };
  const Page = pages[activePage] || SignalsPage;
  return <Page {...props} />;
}


function ReportPage({ token, accent, card, text, muted, border, T, showToast, lang }) {
  const todayStr = new Date().toISOString().split('T')[0];
  const [report, setReport] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [selDate, setSelDate] = React.useState(todayStr);

  const loadReport = (d) => {
    setLoading(true);
    api('/report/daily?date=' + d, 'GET', null, token).then(r => {
      if (r && !r.error) setReport(r);
      setLoading(false);
    });
  };

  React.useEffect(() => { loadReport(todayStr); }, [token]);

  const sendReport = async () => {
    const r = await api('/report/send', 'POST', {}, token);
    if (r && r.ok) showToast('✅ تم إرسال التقرير على تلغرام');
    else showToast('خطأ في الإرسال', 'error');
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: muted }}>...</div>;
  if (!report) return <div style={{ padding: 40, textAlign: 'center', color: muted }}>لا يوجد بيانات</div>;

  const pnlColor = report.pnl >= 0 ? '#10b981' : '#ef4444';

  return (
    <div className="fade" style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 22, fontWeight: 900, color: text }}>📊 التقرير اليومي - {report.date}</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input type="date" value={selDate} max={todayStr} onChange={e => { setSelDate(e.target.value); loadReport(e.target.value); }}
            style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, background: card, color: text, fontSize: 13 }} />
          <button onClick={sendReport} style={{ background: accent, color: '#000', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, cursor: 'pointer' }}>
            📤 إرسال تلغرام
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'الإشارات', value: report.signals, icon: '📈', color: accent },
          { label: 'صفقات منفذة', value: report.executed_trades || 0, icon: '🔄', color: accent },
          { label: 'صفقات مغلقة', value: report.closed_trades, icon: '✅', color: accent },
          { label: 'مراكز مفتوحة', value: report.open_positions, icon: '📂', color: accent },
          { label: 'ر/خ اليوم', value: (report.pnl >= 0 ? '+' : '') + report.pnl + '$', icon: '💰', color: pnlColor },
          { label: 'نسبة الفوز', value: report.win_rate + '%', icon: '🎯', color: report.win_rate >= 50 ? '#10b981' : '#ef4444' },
          { label: 'رابحة', value: report.winning || 0, icon: '🟢', color: '#10b981' },
          { label: 'خاسرة', value: report.losing || 0, icon: '🔴', color: '#ef4444' },
        ].map((s, i) => (
          <div key={i} className="card" style={{ textAlign: 'center', padding: 14 }}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: muted, marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {report.open_list?.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ color: text, marginBottom: 12 }}>📂 المراكز المفتوحة ({report.open_positions})</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['الرمز','كمية','سعر الدخول','السعر الحالي','ر/خ'].map(h => <th key={h} style={{ padding: '8px 10px', color: muted, fontSize: 12, textAlign: 'center' }}>{h}</th>)}</tr></thead>
            <tbody>{report.open_list.map((t, i) => {
              const upnl = t.price && t.entry_price ? ((t.price - t.entry_price) * (t.qty || 1)).toFixed(2) : null;
              return (
                <tr key={i} style={{ borderTop: `1px solid ${border}` }}>
                  <td style={{ padding: '8px 10px', fontWeight: 700, color: text, textAlign: 'center' }}>{t.symbol}</td>
                  <td style={{ padding: '8px 10px', color: text, textAlign: 'center' }}>{t.qty}</td>
                  <td style={{ padding: '8px 10px', color: text, textAlign: 'center' }}>${t.entry_price?.toFixed(2)}</td>
                  <td style={{ padding: '8px 10px', color: text, textAlign: 'center' }}>${t.price?.toFixed(2) || '-'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'center', color: upnl >= 0 ? '#10b981' : '#ef4444', fontWeight: 700 }}>{upnl ? (upnl >= 0 ? '+' : '') + upnl + '$' : '-'}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}

      {report.signals_list?.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ color: text, marginBottom: 12 }}>📈 إشارات اليوم ({report.signals})</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['الرمز','إشارة','السعر','هدف','وقف','ثقة','MA'].map(h => <th key={h} style={{ padding: '8px 10px', color: muted, fontSize: 12, textAlign: 'center' }}>{h}</th>)}</tr></thead>
            <tbody>{report.signals_list.map((s, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${border}` }}>
                <td style={{ padding: '8px 10px', fontWeight: 700, color: text, textAlign: 'center' }}>{s.symbol}</td>
                <td style={{ padding: '8px 10px', textAlign: 'center' }}><span style={{ background: s.action==='BUY' ? '#10b98122' : '#ef444422', color: s.action==='BUY' ? '#10b981' : '#ef4444', borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>{s.action==='BUY' ? 'شراء' : 'بيع'}</span></td>
                <td style={{ padding: '8px 10px', color: text, textAlign: 'center' }}>${s.price?.toFixed(2)}</td>
                <td style={{ padding: '8px 10px', color: '#10b981', textAlign: 'center' }}>${s.tp?.toFixed(2)}</td>
                <td style={{ padding: '8px 10px', color: '#ef4444', textAlign: 'center' }}>${s.sl?.toFixed(2)}</td>
                <td style={{ padding: '8px 10px', textAlign: 'center' }}><span style={{ color: s.confidence >= 88 ? '#10b981' : s.confidence >= 75 ? '#f59e0b' : '#ef4444', fontWeight: 700 }}>{s.confidence}%</span></td>
                <td style={{ padding: '8px 10px', color: muted, textAlign: 'center', fontSize: 12 }}>{s.ma}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {report.trades_list?.length > 0 && (
        <div className="card">
          <h3 style={{ color: text, marginBottom: 12 }}>✅ الصفقات المنفذة ({report.trades_list.length})</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['الرمز','نوع','كمية','سعر الدخول','السعر','ر/خ','الحالة'].map(h => <th key={h} style={{ padding: '8px 10px', color: muted, fontSize: 12, textAlign: 'center' }}>{h}</th>)}</tr></thead>
            <tbody>{report.trades_list.map((t, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${border}` }}>
                <td style={{ padding: '8px 10px', fontWeight: 700, color: text, textAlign: 'center' }}>{t.symbol}</td>
                <td style={{ padding: '8px 10px', textAlign: 'center' }}><span style={{ background: t.action==='BUY' ? '#10b98122' : '#ef444422', color: t.action==='BUY' ? '#10b981' : '#ef4444', borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>{t.action==='BUY' ? 'شراء' : 'بيع'}</span></td>
                <td style={{ padding: '8px 10px', color: text, textAlign: 'center' }}>{t.qty}</td>
                <td style={{ padding: '8px 10px', color: text, textAlign: 'center' }}>${t.entry_price?.toFixed(2) || '-'}</td>
                <td style={{ padding: '8px 10px', color: text, textAlign: 'center' }}>${t.price?.toFixed(2)}</td>
                <td style={{ padding: '8px 10px', textAlign: 'center', color: t.pnl >= 0 ? '#10b981' : '#ef4444', fontWeight: 700 }}>{t.pnl ? (t.pnl >= 0 ? '+' : '') + t.pnl + '$' : '-'}</td>
                <td style={{ padding: '8px 10px', textAlign: 'center' }}><span style={{ background: t.status==='closed' ? '#10b98122' : '#f59e0b22', color: t.status==='closed' ? '#10b981' : '#f59e0b', borderRadius: 6, padding: '2px 8px', fontSize: 11 }}>{t.status==='closed' ? 'مغلقة' : 'مفتوحة'}</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function ProfilePage({ token, accent, card, text, muted, border, T, showToast, lang }) {
  const [oldPass, setOldPass] = React.useState('');
  const [newPass, setNewPass] = React.useState('');
  const [confirmPass, setConfirmPass] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const handleChange = async () => {
    if (!oldPass || !newPass || !confirmPass) return showToast(lang==='ar' ? 'يرجى ملء جميع الحقول' : 'Please fill all fields', 'error');
    if (newPass !== confirmPass) return showToast(lang==='ar' ? 'كلمة السر الجديدة غير متطابقة' : 'Passwords do not match', 'error');
    if (newPass.length < 6) return showToast(lang==='ar' ? 'كلمة السر يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters', 'error');
    setLoading(true);
    const r = await api('/auth/change-password', 'POST', { old_password: oldPass, new_password: newPass }, token);
    setLoading(false);
    if (r && r.message) {
      showToast(r.message, 'success');
      setOldPass(''); setNewPass(''); setConfirmPass('');
    } else {
      showToast(r?.error || 'Error', 'error');
    }
  };

  return (
    <div style={{ maxWidth: 420, margin: '40px auto', padding: 24, background: card, borderRadius: 16, border: `1px solid ${border}` }}>
      <h2 style={{ color: text, marginBottom: 24, textAlign: 'center' }}>{lang==='ar' ? '🔒 تغيير كلمة السر' : '🔒 Change Password'}</h2>
      {[
        { label: lang==='ar' ? 'كلمة السر الحالية' : 'Current Password', val: oldPass, set: setOldPass },
        { label: lang==='ar' ? 'كلمة السر الجديدة' : 'New Password', val: newPass, set: setNewPass },
        { label: lang==='ar' ? 'تأكيد كلمة السر' : 'Confirm Password', val: confirmPass, set: setConfirmPass },
      ].map(({ label, val, set }, i) => (
        <div key={i} style={{ marginBottom: 16 }}>
          <div style={{ color: muted, fontSize: 13, marginBottom: 6 }}>{label}</div>
          <input type="password" value={val} onChange={e => set(e.target.value)}
            style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${border}`, background: 'transparent', color: text, fontSize: 14, boxSizing: 'border-box' }} />
        </div>
      ))}
      <button onClick={handleChange} disabled={loading}
        style={{ width: '100%', padding: '12px 0', borderRadius: 8, background: accent, color: '#000', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 8 }}>
        {loading ? '...' : (lang==='ar' ? 'تغيير كلمة السر' : 'Change Password')}
      </button>
    </div>
  );
}

function SignalsPage({ token, user, accent, bg, card, text, muted, border, T, showToast }) {
  const [signals, setSignals] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [filter, setFilter] = React.useState("all");
  const [minConf, setMinConf] = React.useState(0);
  const [account, setAccount] = React.useState(null);
  const [slEnabled, setSlEnabled] = React.useState(() => localStorage.getItem('sl_enabled') !== 'false');
  const [tradeModal, setTradeModal] = React.useState(null);
  const [tradeQty, setTradeQty] = React.useState(1);
  const [tradeType, setTradeType] = React.useState('market');
  const [tradePrice, setTradePrice] = React.useState('');
  const [tradeLoading, setTradeLoading] = React.useState(false);

  const executeTrade = async () => {
    if (!tradeModal) return;
    setTradeLoading(true);
    const body = { symbol: tradeModal.symbol, qty: tradeQty, side: tradeModal.action.toLowerCase(), type: tradeType, sl: tradeModal.sl, tp: tradeModal.tp, price: tradeType === 'limit' ? parseFloat(tradePrice) : null };
    const r = await api('/alpaca/trade', 'POST', body, token);
    setTradeLoading(false);
    if (r && !r.error) { showToast('✅ تم إرسال الأمر بنجاح'); setTradeModal(null); }
    else showToast(r?.error || 'خطأ في التداول', 'error');
  };
  React.useEffect(() => {
    api("/alpaca/data", "GET", null, token).then(r => { if (r && !r.error) setAccount(r); });
    api("/signals", "GET", null, token).then(r => { if (Array.isArray(r)) setSignals(r); });
    
  }, [token]);

  const toggleStopLoss = async () => {
    const newVal = !slEnabled;
    const r = await api('/user/stop-loss', 'POST', { enabled: newVal }, token);
    if (r && r.ok) { setSlEnabled(newVal); localStorage.setItem('sl_enabled', String(newVal)); showToast(newVal ? '✅ تم تفعيل وقف الخسارة' : '⚠️ تم إلغاء وقف الخسارة'); }
  };
  const scan = () => {
    setLoading(true);
    api("/signals/scan", "GET", null, token).then(r => {
      if (Array.isArray(r)) setSignals(r);
      setLoading(false);
    });
  };
  const filtered = signals.filter(s => {
    if (filter === "strong" && s.confidence < 88) return false;
    if (filter === "medium" && (s.confidence < 75 || s.confidence >= 88)) return false;
    if (s.confidence < minConf) return false;
    return true;
  });
  const confColor = (c) => c >= 90 ? "#10b981" : c >= 80 ? "#f59e0b" : "#ef4444";
  const maColor = (ma) => ma && ma.includes("200") ? "#10b981" : ma && ma.includes("150") ? "#3b82f6" : ma && ma.includes("50") ? "#f59e0b" : "#94a3b8";
  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <h1 style={{ fontSize: 22, fontWeight: 900, color: text, margin: 0 }}>📡 {T.signals}</h1>
        <div onClick={toggleStopLoss} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', background: card, border: `1px solid ${slEnabled ? accent : '#ef4444'}`, borderRadius: 20, padding: '6px 14px' }}>
          <div style={{ width: 36, height: 20, borderRadius: 10, background: slEnabled ? accent : '#ef444444', position: 'relative', transition: 'all 0.3s' }}>
            <div style={{ position: 'absolute', top: 2, left: slEnabled ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: slEnabled ? '#000' : '#ef4444', transition: 'all 0.3s' }} />
          </div>
          <span style={{ fontSize: 13, color: slEnabled ? accent : '#ef4444', fontWeight: 600 }}>🛑 {slEnabled ? 'وقف الخسارة مفعّل' : 'وقف الخسارة معطّل'}</span>
        </div>
        <button className="btn btn-gold" onClick={scan} disabled={loading} style={{ fontSize: 15, padding: "10px 28px" }}>
          {loading ? "⏳ جاري الفحص..." : "🔍 فحص السوق"}
        </button>
      </div>
      {account && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 20 }}>
          {[["💰 الرصيد", "$"+parseFloat(account.equity||0).toLocaleString("en",{minimumFractionDigits:2})],
            ["💵 النقد", "$"+parseFloat(account.cash||0).toLocaleString("en",{minimumFractionDigits:2})],
            ["⚡ الشراء", "$"+parseFloat(account.buying_power||0).toLocaleString("en",{minimumFractionDigits:2})],
            ["📊 إشارات", signals.length]
          ].map(([l,v]) => (
            <div key={l} className="stat-card">
              <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>{l}</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: text }}>{v}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        {[["all","الكل"],["strong","قوية +88%"],["medium","متوسطة"]].map(([val,lbl]) => (
          <button key={val} onClick={() => setFilter(val)}
            style={{ padding: "6px 16px", borderRadius: 20, border: "1.5px solid "+(filter===val ? accent : border),
              background: filter===val ? accent : "transparent", color: filter===val ? "#fff" : text,
              cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{lbl}</button>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginRight: "auto" }}>
          <span style={{ fontSize: 12, color: muted }}>ثقة أدنى: {minConf}%</span>
          <input type="range" min="0" max="95" step="5" value={minConf} onChange={e => setMinConf(+e.target.value)}
            style={{ width: 100, accentColor: accent }} />
        </div>
      </div>
      {filtered.length === 0 && !loading && (
        <div style={{ textAlign: "center", padding: 60, color: muted }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📡</div>
          <div style={{ fontSize: 16 }}>اضغط "فحص السوق" لعرض الإشارات</div>
        </div>
      )}
      {filtered.length > 0 && (
        <div style={{ background: card, borderRadius: 16, border: "1px solid "+border, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid "+border }}>
                {["السهم","إشارة","السعر","الهدف","وقف الخسارة","المتوسط","الثقة","VR",""].map(h => (
                  <th key={h} style={{ padding: "12px 14px", color: muted, fontWeight: 700, textAlign: "center", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => (
                <tr key={i} style={{ borderBottom: "1px solid "+border }}
                  onMouseEnter={e => e.currentTarget.style.background = border}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "12px 14px", fontWeight: 900, color: text, textAlign: "center", fontSize: 16 }}>{s.symbol}</td>
                  <td style={{ padding: "12px 14px", textAlign: "center" }}>
                    <span style={{ background: s.action==="BUY" ? "#10b98122" : "#ef444422",
                      color: s.action==="BUY" ? "#10b981" : "#ef4444",
                      borderRadius: 100, padding: "3px 12px", fontWeight: 700, fontSize: 13 }}>
                      {s.action==="BUY" ? "🟢 شراء" : "🔴 بيع"}
                    </span>
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center", fontWeight: 700, color: text }}>${s.price}</td>
                  <td style={{ padding: "12px 14px", textAlign: "center", color: "#10b981", fontWeight: 700 }}>${s.tp}</td>
                  <td style={{ padding: "12px 14px", textAlign: "center", color: "#ef4444", fontWeight: 700 }}>${s.sl}</td>
                  <td style={{ padding: "12px 14px", textAlign: "center" }}>
                    <span style={{ background: maColor(s.ma)+"22", color: maColor(s.ma), borderRadius: 8, padding: "2px 8px", fontSize: 12, fontWeight: 700 }}>{s.ma}</span>
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
                      <div style={{ width: 60, height: 6, borderRadius: 3, background: border, overflow: "hidden" }}>
                        <div style={{ width: s.confidence+"%", height: "100%", background: confColor(s.confidence), borderRadius: 3 }} />
                      </div>
                      <span style={{ color: confColor(s.confidence), fontWeight: 700, fontSize: 13 }}>{s.confidence}%</span>
                    </div>
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center", color: muted, fontSize: 13 }}>{s.vr}x</td>
                  <td style={{ padding: "12px 14px", textAlign: "center" }}>
                    <button className="btn btn-gold" style={{ padding: "5px 14px", fontSize: 12 }}
                      onClick={() => { setTradeModal(s); setTradeQty(1); setTradeType('market'); setTradePrice(s.price?.toString() || ''); }}>
                      تداول
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    {tradeModal && (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#1a1a2e', borderRadius: 16, padding: 28, width: 340, border: '1px solid #2a2a4a' }}>
          <h3 style={{ color: '#fff', marginBottom: 20, textAlign: 'center' }}>🔄 {tradeModal.action} {tradeModal.symbol}</h3>
          <div style={{ marginBottom: 14 }}>
            <div style={{ color: '#aaa', fontSize: 13, marginBottom: 6 }}>الكمية</div>
            <input type="number" min="1" value={tradeQty} onChange={e => setTradeQty(parseInt(e.target.value) || 1)}
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #2a2a4a', background: '#0d0d1a', color: '#fff', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ color: '#aaa', fontSize: 13, marginBottom: 6 }}>نوع الأمر</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {['market', 'limit'].map(t => (
                <div key={t} onClick={() => setTradeType(t)} style={{ flex: 1, padding: '8px 0', textAlign: 'center', borderRadius: 8, cursor: 'pointer', background: tradeType === t ? '#10b981' : '#1a2a3a', color: tradeType === t ? '#000' : '#aaa', fontWeight: 600, fontSize: 13 }}>
                  {t === 'market' ? 'Market' : 'Limit'}
                </div>
              ))}
            </div>
          </div>
          {tradeType === 'limit' && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: '#aaa', fontSize: 13, marginBottom: 6 }}>السعر</div>
              <input type="number" value={tradePrice} onChange={e => setTradePrice(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #2a2a4a', background: '#0d0d1a', color: '#fff', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button onClick={() => setTradeModal(null)} style={{ flex: 1, padding: 12, borderRadius: 8, border: '1px solid #2a2a4a', background: 'transparent', color: '#aaa', cursor: 'pointer' }}>إلغاء</button>
            <button onClick={executeTrade} disabled={tradeLoading} style={{ flex: 2, padding: 12, borderRadius: 8, border: 'none', background: '#10b981', color: '#000', fontWeight: 700, cursor: 'pointer' }}>
              {tradeLoading ? '...' : `تنفيذ ${tradeModal.action}`}
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
}

function PortfolioPage({ token, accent, bg, card, text, muted, border, T, showToast }) {
  const [sellModal, setSellModal] = React.useState(null);
  const [sellQty, setSellQty] = React.useState(1);
  const [sellType, setSellType] = React.useState('market');
  const [sellPrice, setSellPrice] = React.useState('');
  const [sellLoading, setSellLoading] = React.useState(false);

  const executeSell = async () => {
    if (!sellModal) return;
    setSellLoading(true);
    const body = { qty: sellQty, type: sellType, price: sellType === 'limit' ? parseFloat(sellPrice) : null };
    const r = await api('/alpaca/close/'+sellModal.symbol, 'POST', body, token);
    setSellLoading(false);
    if (r && r.message) { showToast('✅ '+r.message); setSellModal(null); }
    else showToast(r?.error || 'خطأ', 'error');
  };
  const [positions, setPositions] = useState([]);
  const [account, setAccount] = useState(null);

  useEffect(() => {
    api('/alpaca/data', 'GET', null, token).then(r => {
      if (r && !r.error) { setAccount(r); setPositions(r.positions || []); }
    });
  }, [token]);

  return (
    <div className="fade" style={{ direction: T.dir }}>
      <h1 style={{ fontSize: 22, fontWeight: 900, color: text, marginBottom: 24 }}>💼 {T.portfolio}</h1>
      {account && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 16, marginBottom: 24 }}>
          {[['الرصيد', `$${parseFloat(account.equity || 0).toLocaleString('en', { minimumFractionDigits: 2 })}`], ['النقد', `$${parseFloat(account.cash || 0).toLocaleString('en', { minimumFractionDigits: 2 })}`], ['القوة الشرائية', `$${parseFloat(account.buying_power || 0).toLocaleString('en', { minimumFractionDigits: 2 })}`]].map(([l, v]) => (
            <div key={l} className="stat-card"><div style={{ fontSize: 12, color: muted, marginBottom: 4 }}>{l}</div><div style={{ fontSize: 20, fontWeight: 900, color: text }}>{v}</div></div>
          ))}
        </div>
      )}
      {positions.length > 0 ? (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['الرمز', 'الكمية', 'سعر الدخول', 'السعر الحالي', 'الربح/الخسارة'].map(h => <th key={h} style={{ padding: '10px', fontSize: 12, color: muted, textAlign: 'right', borderBottom: `1px solid ${border}` }}>{h}</th>)}</tr></thead>
            <tbody>{positions.map((p, i) => <tr key={i}>
  <td style={{ padding: 10, color: text, fontWeight: 700 }}>{p.symbol}</td>
  <td style={{ padding: 10, color: text }}>{p.qty}</td>
  <td style={{ padding: 10, color: text }}>${parseFloat(p.avg_entry_price || 0).toFixed(2)}</td>
  <td style={{ padding: 10, color: text }}>${parseFloat(p.current_price || 0).toFixed(2)}</td>
  <td style={{ padding: 10, color: parseFloat(p.unrealized_pl) >= 0 ? '#10b981' : '#ef4444', fontWeight: 700 }}>${parseFloat(p.unrealized_pl || 0).toFixed(2)}</td>
  <td style={{ padding: 10 }}>
    <button className="btn" style={{ background: '#ef444422', color: '#ef4444', border: '1px solid #ef4444', padding: '4px 12px', fontSize: 12, borderRadius: 8, cursor: 'pointer' }}
      onClick={() => { setSellModal(p); setSellQty(parseInt(p.qty) || 1); setSellType('market'); setSellPrice(p.current_price?.toString() || ''); }}>
      🔴 بيع
    </button>
  </td>
</tr>)}</tbody>
          </table>
        </div>
      ) : <div style={{ color: muted, textAlign: 'center', padding: 60 }}>لا توجد مراكز مفتوحة</div>}
    {sellModal && (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#1a1a2e', borderRadius: 16, padding: 28, width: 340, border: '1px solid #2a2a4a' }}>
          <h3 style={{ color: '#fff', marginBottom: 20, textAlign: 'center' }}>🔴 بيع {sellModal.symbol}</h3>
          <div style={{ marginBottom: 14 }}>
            <div style={{ color: '#aaa', fontSize: 13, marginBottom: 6 }}>الكمية (متاح: {sellModal.qty})</div>
            <input type="number" min="1" max={sellModal.qty} value={sellQty} onChange={e => setSellQty(parseInt(e.target.value) || 1)}
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #2a2a4a', background: '#0d0d1a', color: '#fff', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ color: '#aaa', fontSize: 13, marginBottom: 6 }}>نوع الأمر</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {['market', 'limit'].map(t => (
                <div key={t} onClick={() => setSellType(t)} style={{ flex: 1, padding: '8px 0', textAlign: 'center', borderRadius: 8, cursor: 'pointer', background: sellType === t ? '#ef4444' : '#1a2a3a', color: sellType === t ? '#fff' : '#aaa', fontWeight: 600, fontSize: 13 }}>
                  {t === 'market' ? 'Market' : 'Limit'}
                </div>
              ))}
            </div>
          </div>
          {sellType === 'limit' && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: '#aaa', fontSize: 13, marginBottom: 6 }}>السعر</div>
              <input type="number" value={sellPrice} onChange={e => setSellPrice(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #2a2a4a', background: '#0d0d1a', color: '#fff', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button onClick={() => setSellModal(null)} style={{ flex: 1, padding: 12, borderRadius: 8, border: '1px solid #2a2a4a', background: 'transparent', color: '#aaa', cursor: 'pointer' }}>إلغاء</button>
            <button onClick={executeSell} disabled={sellLoading} style={{ flex: 2, padding: 12, borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
              {sellLoading ? '...' : 'تنفيذ البيع'}
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
}

function OrdersPage({ token, accent, bg, card, text, muted, border, T }) {
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api('/trades', 'GET', null, token).then(r => { if (Array.isArray(r)) setOrders(r); });
  }, [token]);

  return (
    <div className="fade" style={{ direction: T.dir }}>
      <h1 style={{ fontSize: 22, fontWeight: 900, color: text, marginBottom: 24 }}>📋 {T.orders}</h1>
      {orders.length > 0 ? (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['التاريخ', 'الرمز', 'النوع', 'السعر', 'الكمية', 'الحالة'].map(h => <th key={h} style={{ padding: 10, fontSize: 12, color: muted, textAlign: 'right', borderBottom: `1px solid ${border}` }}>{h}</th>)}</tr></thead>
            <tbody>{orders.map((o, i) => <tr key={i} style={{ borderBottom: `1px solid ${border}` }}><td style={{ padding: 10, fontSize: 12, color: muted }}>{new Date(o.created_at).toLocaleDateString('ar')}</td><td style={{ padding: 10, color: text, fontWeight: 700 }}>{o.symbol}</td><td style={{ padding: 10 }}><span style={{ background: o.action === 'BUY' ? '#10b98122' : '#ef444422', color: o.action === 'BUY' ? '#10b981' : '#ef4444', borderRadius: 100, padding: '2px 8px', fontSize: 12 }}>{o.action === 'BUY' ? 'شراء' : 'بيع'}</span></td><td style={{ padding: 10, color: text }}>${o.price}</td><td style={{ padding: 10, color: text }}>{o.qty}</td><td style={{ padding: 10, color: '#10b981', fontSize: 12 }}>{o.status}</td></tr>)}</tbody>
          </table>
        </div>
      ) : <div style={{ color: muted, textAlign: 'center', padding: 60 }}>لا توجد أوامر</div>}
    </div>
  );
}

function BotPage({ token, accent, bg, card, text, muted, border, T, showToast }) {
  const [botStatus, setBotStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [slEnabled, setSlEnabled] = useState(() => localStorage.getItem('sl_enabled') !== 'false');
  const [botSettings, setBotSettings] = useState({ max_trades: 5, max_profit: 0, max_loss: 0 });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    api('/bot/status', 'GET', null, token).then(r => { if (r) setBotStatus(r); });
    api('/bot/settings', 'GET', null, token).then(r => { if (r && !r.error) setBotSettings(r); });

  }, [token]);

  const toggleStopLoss = async () => {
    const newVal = !slEnabled;
    const r = await api('/user/stop-loss', 'POST', { enabled: newVal }, token);
    if (r && r.ok) { setSlEnabled(newVal); showToast(newVal ? '✅ تم تفعيل وقف الخسارة' : '⚠️ تم إلغاء وقف الخسارة'); }
  };

  const toggleBot = async () => {
    setLoading(true);
    const action = botStatus?.running ? 'stop' : 'start';
    const r = await api(`/bot/${action}`, 'POST', {}, token);
    if (r) { setBotStatus(r); showToast(action === 'start' ? '✅ البوت يعمل الآن' : '⏹️ تم إيقاف البوت'); }
    setLoading(false);
  };

  return (
    <div className="fade" style={{ direction: T.dir }}>
      <h1 style={{ fontSize: 22, fontWeight: 900, color: text, marginBottom: 24 }}>🤖 {T.bot}</h1>
      <div className="card" style={{ maxWidth: 500 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: botStatus?.running ? '#10b981' : '#ef4444' }} />
          <span style={{ color: text, fontWeight: 700 }}>{botStatus?.running ? 'البوت يعمل' : 'البوت متوقف'}</span>
        </div>
        <div className="card" style={{ maxWidth: 500, marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px' }}>
        <div>
          <div style={{ fontWeight: 700, color: text, marginBottom: 4 }}>🛑 وقف الخسارة</div>
          <div style={{ fontSize: 13, color: muted }}>إيقاف تلقائي عند الخسارة</div>
        </div>
        <div onClick={toggleStopLoss} style={{ width: 48, height: 26, borderRadius: 13, background: slEnabled ? accent : '#ef444444', cursor: 'pointer', position: 'relative', transition: 'all 0.3s' }}>
          <div style={{ position: 'absolute', top: 3, left: slEnabled ? 24 : 3, width: 20, height: 20, borderRadius: '50%', background: slEnabled ? '#000' : '#ef4444', transition: 'all 0.3s' }} />
        </div>
      </div>
      <div className="card" style={{ maxWidth: 500, marginTop: 16, padding: '16px 20px' }}>
        <div style={{ fontWeight: 700, color: text, marginBottom: 14 }}>⚙️ إعدادات البوت</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
          {[
            { label: 'حد الصفقات/يوم', key: 'max_trades', placeholder: '5' },
            { label: 'حد الربح ($)', key: 'max_profit', placeholder: '0 = غير محدد' },
            { label: 'حد الخسارة ($)', key: 'max_loss', placeholder: '0 = غير محدد' },
          ].map(({ label, key, placeholder }) => (
            <div key={key}>
              <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>{label}</div>
              <input type="number" min="0" value={botSettings[key]} onChange={e => setBotSettings({ ...botSettings, [key]: parseFloat(e.target.value) || 0 })}
                placeholder={placeholder}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${border}`, background: 'transparent', color: text, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
          ))}
        </div>
        <button onClick={async () => { setSavingSettings(true); const r = await api('/bot/settings', 'POST', botSettings, token); setSavingSettings(false); if (r?.ok) showToast('✅ تم حفظ الإعدادات'); }} style={{ background: accent, color: '#000', border: 'none', borderRadius: 8, padding: '8px 20px', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
          {savingSettings ? '...' : 'حفظ الإعدادات'}
        </button>
      </div>
      {botStatus && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            {[['الصفقات اليوم', botStatus.trades_today || 0], ['إجمالي الصفقات', botStatus.total_trades || 0], ['آخر فحص', botStatus.last_scan || '—'], ['الاستراتيجية', botStatus.strategy || 'EMA']].map(([l, v]) => (
              <div key={l} style={{ background: bg, borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>{l}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: text }}>{v}</div>
              </div>
            ))}
          </div>
        )}
        <button className="btn" style={{ width: '100%', background: botStatus?.running ? '#ef444422' : accent, color: botStatus?.running ? '#ef4444' : '#000', border: botStatus?.running ? '1px solid #ef4444' : 'none' }} onClick={toggleBot} disabled={loading}>
          {loading ? '⏳...' : botStatus?.running ? '⏹️ إيقاف البوت' : '▶️ تشغيل البوت'}
        </button>
      </div>
    </div>
  );
}

function AlpacaPage({ token, accent, bg, card, text, muted, border, T, showToast }) {
  const [connected, setConnected] = useState(false);
  const [account, setAccount] = useState(null);
  const [form, setForm] = useState({ key: '', secret: '', paper: true });

  useEffect(() => {
    const saved = localStorage.getItem('tb_alpaca');
    if (saved) { const d = JSON.parse(saved); setForm(d); }
    api('/alpaca/data', 'GET', null, token).then(r => { if (r && !r.error) { setAccount(r); setConnected(true); } });
  }, [token]);

  const connect = async () => {
    const r = await api('/alpaca/connect', 'POST', form, token);
    if (r && !r.error) { setConnected(true); setAccount(r); localStorage.setItem('tb_alpaca', JSON.stringify(form)); showToast('✅ تم الربط بنجاح'); }
    else showToast(r?.error || 'خطأ في الربط', 'err');
  };

  const disconnect = async () => {
    await api('/alpaca/disconnect', 'POST', {}, token);
    setConnected(false); setAccount(null); localStorage.removeItem('tb_alpaca');
  };

  return (
    <div className="fade" style={{ direction: T.dir }}>
      <h1 style={{ fontSize: 22, fontWeight: 900, color: text, marginBottom: 24 }}>🔗 {T.alpaca}</h1>
      {connected && account ? (
        <div className="card" style={{ maxWidth: 400, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <div style={{ color: '#10b981', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>متصل بنجاح!</div>
          <div style={{ background: bg, borderRadius: 10, padding: '12px 20px', marginBottom: 20, display: 'inline-block' }}>
            <span style={{ color: muted, fontSize: 13 }}>الرصيد: </span>
            <span style={{ color: '#10b981', fontWeight: 700, fontSize: 16 }}>${parseFloat(account.equity || 0).toLocaleString('en', { minimumFractionDigits: 2 })}</span>
          </div>
          <br />
          <button className="btn btn-outline" style={{ borderColor: '#ef4444', color: '#ef4444' }} onClick={disconnect}>قطع الاتصال</button>
        </div>
      ) : (
        <div className="card" style={{ maxWidth: 400 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {[['Paper 🧪', true], ['Live 💰', false]].map(([l, v]) => (
              <button key={l} className="btn" style={{ flex: 1, background: form.paper === v ? accent : 'transparent', color: form.paper === v ? '#000' : muted, border: `1px solid ${border}` }} onClick={() => setForm(f => ({ ...f, paper: v }))}>{l}</button>
            ))}
          </div>
          {!form.paper && <div style={{ background: '#2d0a0a', border: '1px solid #7f1d1d', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#fca5a5', marginBottom: 16 }}>⚠️ Live Trading يستخدم أموال حقيقية</div>}
          <div style={{ marginBottom: 14 }}><label style={{ fontSize: 12, color: muted, display: 'block', marginBottom: 6 }}>API Key ID</label><input className="inp" placeholder="PK..." value={form.key} onChange={e => setForm(f => ({ ...f, key: e.target.value }))} style={{ direction: 'ltr' }} /></div>
          <div style={{ marginBottom: 20 }}><label style={{ fontSize: 12, color: muted, display: 'block', marginBottom: 6 }}>Secret Key</label><input className="inp" type="password" placeholder="••••••••" value={form.secret} onChange={e => setForm(f => ({ ...f, secret: e.target.value }))} /></div>
          <button className="btn btn-gold" style={{ width: '100%' }} onClick={connect}>ربط الحساب ←</button>
        </div>
      )}
    </div>
  );
}

function AlertsPage({ token, accent, bg, card, text, muted, border, T, showToast }) {
  const [alerts, setAlerts] = useState([]);
  const [form, setForm] = useState({ symbol: '', price: '', type: 'above' });
  const WATCHLIST = ["AAPL","MSFT","GOOGL","AMZN","NVDA","META","TSLA","AMD","INTC","CRM","SOFI","PLTR","RIVN","LCID","PLUG","MARA","RIOT","CLSK","HUT","BTBT","SNDX","FATE","ISPC","PRAX","ACRS","BB","BORR","OPEN","FCEL","SPWR","RUN","NOVA","CGNT","KOPN","ACHV","BW","CHPT","SPCE","QUBT","NFLX","DIS","PYPL","SQ","UBER","LYFT","SNAP","SPOT","COIN","HOOD"];
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    api('/alerts', 'GET', null, token).then(r => { if (Array.isArray(r)) setAlerts(r); });
  }, [token]);

  const addAlert = async () => {
    const r = await api('/alerts', 'POST', form, token);
    if (r && !r.error) { setAlerts(a => [...a, r]); setForm({ symbol: '', price: '', type: 'above' }); showToast('✅ تم إضافة التنبيه'); }
  };

  const deleteAlert = async (id) => {
    await api(`/alerts/${id}`, 'DELETE', null, token);
    setAlerts(a => a.filter(x => x.id !== id));
  };

  return (
    <div className="fade" style={{ direction: T.dir }}>
      <h1 style={{ fontSize: 22, fontWeight: 900, color: text, marginBottom: 24 }}>🔔 {T.alerts}</h1>
      <div className="card" style={{ maxWidth: 500, marginBottom: 24 }}>
        <h3 style={{ color: text, marginBottom: 16, fontSize: 15 }}>إضافة تنبيه جديد</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div><label style={{ fontSize: 12, color: muted, display: 'block', marginBottom: 4 }}>الرمز</label><div style={{ position: 'relative' }}>
          <input className="inp" placeholder="AAPL" value={form.symbol}
            onChange={e => { const v = e.target.value.toUpperCase(); setForm(f => ({ ...f, symbol: v })); setSuggestions(v ? WATCHLIST.filter(s => s.startsWith(v)) : []); }}
            style={{ direction: 'ltr', width: '100%' }} />
          {suggestions.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: card, border: '1px solid '+border, borderRadius: 8, zIndex: 100, maxHeight: 180, overflowY: 'auto' }}>
              {suggestions.map(s => (
                <div key={s} onClick={() => { setForm(f => ({ ...f, symbol: s })); setSuggestions([]); }}
                  style={{ padding: '8px 12px', cursor: 'pointer', color: text, fontSize: 14 }}
                  onMouseEnter={e => e.target.style.background = border}
                  onMouseLeave={e => e.target.style.background = 'transparent'}>{s}</div>
              ))}
            </div>
          )}
        </div></div>
          <div><label style={{ fontSize: 12, color: muted, display: 'block', marginBottom: 4 }}>السعر المستهدف</label><input className="inp" type="number" placeholder="150.00" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} /></div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: muted, display: 'block', marginBottom: 4 }}>النوع</label>
          <select className="inp" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
            <option value="above">أعلى من</option>
            <option value="below">أقل من</option>
          </select>
        </div>
        <button className="btn btn-gold" onClick={addAlert}>+ إضافة تنبيه</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {alerts.map(a => (
          <div key={a.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px' }}>
            <div>
              <span style={{ fontWeight: 700, color: text, marginLeft: 12 }}>{a.symbol}</span>
              <span style={{ color: muted, fontSize: 13 }}>{a.alert_type === 'above' ? 'أعلى من' : 'أقل من'} ${a.target_price}</span>
            </div>
            <button className="btn" style={{ background: '#ef444422', color: '#ef4444', border: '1px solid #ef444444', padding: '6px 12px' }} onClick={() => deleteAlert(a.id)}>حذف</button>
          </div>
        ))}
        {alerts.length === 0 && <div style={{ color: muted, textAlign: 'center', padding: 40 }}>لا توجد تنبيهات</div>}
      </div>
    </div>
  );
}

function ShariaPage({ token, accent, bg, card, text, muted, border, T }) {
  const [symbol, setSymbol] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async () => {
    if (!symbol) return;
    setLoading(true);
    const r = await api(`/sharia/${symbol}`, 'GET', null, token);
    if (r && !r.error) setResult(r);
    setLoading(false);
  };

  return (
    <div className="fade" style={{ direction: T.dir }}>
      <h1 style={{ fontSize: 22, fontWeight: 900, color: text, marginBottom: 24 }}>🕌 {T.sharia}</h1>
      <div className="card" style={{ maxWidth: 500, marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input className="inp" placeholder="AAPL, MSFT, TSLA..." value={symbol} onChange={e => setSymbol(e.target.value.toUpperCase())} style={{ direction: 'ltr', flex: 1 }} onKeyDown={e => e.key === 'Enter' && check()} />
          <button className="btn btn-gold" onClick={check} disabled={loading}>{loading ? '⏳' : '🔍 فحص'}</button>
        </div>
      </div>
      {result && (
        <div className="card fade" style={{ maxWidth: 500 }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>{result.status === 'halal' ? '✅' : '❌'}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: result.status === 'halal' ? '#10b981' : '#ef4444' }}>{result.status === 'halal' ? 'متوافق شرعياً' : 'غير متوافق'}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            {[['القطاع', result.sector], ['الصناعة', result.industry], ['نسبة الديون', `${result.debt_ratio}%`]].map(([l, v]) => (
              <div key={l} style={{ background: bg, borderRadius: 8, padding: 10, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>{l}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: text }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SupportPage({ token, accent, bg, card, text, muted, border, T, showToast }) {
  const [tickets, setTickets] = useState([]);
  const [form, setForm] = useState({ subject: '', message: '' });
  const [selected, setSelected] = React.useState(null);

  useEffect(() => {
    api('/tickets', 'GET', null, token).then(r => { if (Array.isArray(r)) setTickets(r); });
  }, [token]);

  const submit = async () => {
    const r = await api('/tickets', 'POST', form, token);
    if (r && !r.error) { setTickets(t => [r, ...t]); setForm({ subject: '', message: '' }); showToast('✅ تم إرسال التذكرة'); }
  };

  return (
    <div className="fade" style={{ direction: T.dir }}>
      <h1 style={{ fontSize: 22, fontWeight: 900, color: text, marginBottom: 24 }}>🎫 {T.support}</h1>
      <div className="card" style={{ maxWidth: 500, marginBottom: 24 }}>
        <h3 style={{ color: text, marginBottom: 16, fontSize: 15 }}>تذكرة دعم جديدة</h3>
        <div style={{ marginBottom: 12 }}><input className="inp" placeholder="الموضوع" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} /></div>
        <div style={{ marginBottom: 16 }}><textarea className="inp" placeholder="اشرح مشكلتك..." value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} rows={4} style={{ resize: 'vertical' }} /></div>
        <button className="btn btn-gold" onClick={submit}>إرسال التذكرة</button>
      </div>
        {tickets.map(t => (
          <div key={t.id} className="card" style={{ padding: '14px 18px', cursor: 'pointer' }}
            onClick={() => setSelected(selected?.id === t.id ? null : t)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontWeight: 700, color: text }}>{t.subject}</span>
              <span style={{ background: t.status === 'open' ? '#10b98122' : '#64748b22',
                color: t.status === 'open' ? '#10b981' : muted, borderRadius: 100, padding: '2px 8px', fontSize: 11 }}>
                {t.status === 'open' ? '🟢 مفتوحة' : '✅ مغلقة'}
              </span>
            </div>
            <p style={{ fontSize: 13, color: muted }}>{t.message}</p>
            {selected?.id === t.id && (
              <div style={{ marginTop: 12, borderTop: '1px solid '+border, paddingTop: 12 }}>
                {t.replies && t.replies.length > 0 ? t.replies.map((r, i) => (
                  <div key={i} style={{ background: r.is_admin ? '#10b98122' : bg, borderRadius: 8, padding: '10px 14px', marginBottom: 8, borderRight: r.is_admin ? '3px solid #10b981' : 'none' }}>
                    <div style={{ fontSize: 11, color: r.is_admin ? '#10b981' : muted, marginBottom: 4, fontWeight: 700 }}>
                      {r.is_admin ? '👨‍💼 رد فريق الدعم' : '👤 رسالتك'}
                    </div>
                    <div style={{ fontSize: 14, color: text }}>{r.message}</div>
                  </div>
                )) : (
                  <div style={{ color: muted, fontSize: 13, textAlign: 'center', padding: 10 }}>⏳ في انتظار رد فريق الدعم</div>
                )}
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

function AIPage({ token, accent, bg, card, text, muted, border, T }) {
  const [symbol, setSymbol] = React.useState('');
  const [result, setResult] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const analyze = async () => {
    if (!symbol) return;
    setLoading(true);
    setResult(null);
    const r = await api('/ai/analyze', 'POST', { symbol }, token);
    if (r && !r.error) setResult(r);
    setLoading(false);
  };

  const formatAnalysis = (text) => {
    if (!text) return [];
    return text.split('\n').filter(l => l.trim());
  };

  const lineStyle = (line) => {
    if (line.startsWith('📊') || line.startsWith('📈') || line.startsWith('🔵') || 
        line.startsWith('📉') || line.startsWith('⚡') || line.startsWith('🎯')) {
      return { fontWeight: 900, color: accent, fontSize: 15, marginTop: 16, marginBottom: 6, display: 'block' };
    }
    if (line.startsWith('-')) {
      return { color: text, fontSize: 14, lineHeight: 1.8, paddingRight: 12, display: 'block' };
    }
    if (line.startsWith('⚠️')) {
      return { color: '#f59e0b', fontSize: 12, marginTop: 16, display: 'block', fontStyle: 'italic' };
    }
    return { color: text, fontSize: 14, lineHeight: 1.8, display: 'block' };
  };

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 900, color: text, marginBottom: 24 }}>🧠 {T.ai}</h1>
      
      <div style={{ background: card, borderRadius: 16, border: '1px solid '+border, padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input className="inp" placeholder="AAPL, MSFT, TSLA..." value={symbol}
            onChange={e => setSymbol(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && analyze()}
            style={{ direction: 'ltr', flex: 1, fontSize: 16 }} />
          <button className="btn btn-gold" onClick={analyze} disabled={loading}
            style={{ padding: '10px 24px', fontSize: 15, whiteSpace: 'nowrap' }}>
            {loading ? '⏳ جاري التحليل...' : '🧠 تحليل'}
          </button>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 60, color: muted }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🧠</div>
          <div style={{ fontSize: 16 }}>جاري تحليل السهم بالذكاء الاصطناعي...</div>
        </div>
      )}

      {result && !loading && (
        <div style={{ background: card, borderRadius: 16, border: '1px solid '+border, overflow: 'hidden' }}>
          <div style={{ background: accent, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ color: '#fff', margin: 0, fontSize: 18, fontWeight: 900 }}>📊 {result.symbol} - تحليل الذكاء الاصطناعي</h2>
            <span style={{ color: '#fff', fontSize: 13, opacity: 0.8 }}>Powered by AI</span>
          </div>
          <div style={{ padding: 24 }}>
            <div style={{ background: bg, borderRadius: 12, padding: 20, lineHeight: 2 }}>
              {formatAnalysis(result.analysis).map((line, i) => (
                <span key={i} style={lineStyle(line)}>{line}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ThemePage({ accent, bg, card, text, muted, border, T, theme, setTheme, darkMode, setDarkMode, lang, setLang }) {
  return (
    <div className="fade" style={{ direction: T.dir }}>
      <h1 style={{ fontSize: 22, fontWeight: 900, color: text, marginBottom: 24 }}>⚙️ {T.theme}</h1>
      <div className="card" style={{ maxWidth: 500 }}>
        <h3 style={{ color: text, marginBottom: 16, fontSize: 15 }}>لون التطبيق</h3>
        <div style={{ display: 'flex', gap: 12, marginBottom: 28 }}>
          {Object.entries(THEMES).map(([k, v]) => (
            <div key={k} onClick={() => setTheme(k)} style={{ width: 36, height: 36, borderRadius: '50%', background: v, cursor: 'pointer', border: theme === k ? `3px solid ${text}` : '3px solid transparent', transition: 'transform 0.2s' }} />
          ))}
        </div>
        <h3 style={{ color: text, marginBottom: 16, fontSize: 15 }}>الوضع</h3>
        <div style={{ display: 'flex', gap: 10, marginBottom: 28 }}>
          {[['🌙 داكن', true], ['☀️ فاتح', false]].map(([l, v]) => (
            <button key={l} className="btn" style={{ flex: 1, background: darkMode === v ? accent : 'transparent', color: darkMode === v ? '#000' : muted, border: `1px solid ${border}` }} onClick={() => setDarkMode(v)}>{l}</button>
          ))}
        </div>
        <h3 style={{ color: text, marginBottom: 16, fontSize: 15 }}>اللغة</h3>
        <div style={{ display: 'flex', gap: 10 }}>
          {[['العربية', 'ar'], ['English', 'en']].map(([l, v]) => (
            <button key={l} className="btn" style={{ flex: 1, background: lang === v ? accent : 'transparent', color: lang === v ? '#000' : muted, border: `1px solid ${border}` }} onClick={() => setLang(v)}>{l}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function PlansPage({ accent, bg, card, text, muted, border, T, setActivePage }) {
  const plans = [
    { id: 'free', name: 'مجاني', price: '$0', period: 'للأبد', color: '#64748b', features: ['الإشارات', 'المحفظة', 'الأوامر'] },
    { id: 'pro', name: 'برو', price: '$29', period: '/شهر', color: accent, hot: true, features: ['كل المجاني', 'البوت التلقائي', 'التنبيهات', 'فحص الشرعية', 'الذكاء الاصطناعي', 'الدعم الفني'] },
    { id: 'elite', name: 'إيليت', price: '$99', period: '/شهر', color: '#8b5cf6', features: ['كل البرو', 'ربط Alpaca', 'حسابات متعددة', 'مدير حساب'] },
  ];

  return (
    <div className="fade" style={{ direction: T.dir }}>
      <h1 style={{ fontSize: 22, fontWeight: 900, color: text, marginBottom: 8 }}>💎 {T.plans}</h1>
      <p style={{ color: muted, fontSize: 14, marginBottom: 28 }}>اختر الخطة المناسبة لك</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 20 }}>
        {plans.map(p => (
          <div key={p.id} className="card" style={{ border: `2px solid ${p.hot ? p.color : p.color + '40'}`, position: 'relative', transition: 'transform 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
            {p.hot && <div style={{ position: 'absolute', top: -12, right: 16, background: p.color, borderRadius: 100, padding: '2px 12px', fontSize: 11, fontWeight: 700, color: '#000' }}>⭐ الأشهر</div>}
            <div style={{ fontSize: 16, fontWeight: 700, color: text, marginBottom: 4 }}>{p.name}</div>
            <div style={{ marginBottom: 16 }}>
              <span style={{ fontSize: 32, fontWeight: 900, color: p.color }}>{p.price}</span>
              <span style={{ fontSize: 12, color: muted }}> {p.period}</span>
            </div>
            <div style={{ height: 1, background: border, marginBottom: 14 }} />
            {p.features.map(f => <div key={f} style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: 13, color: muted }}><span style={{ color: p.color }}>✓</span>{f}</div>)}
            <button className="btn" style={{ width: '100%', marginTop: 16, background: p.hot ? p.color : 'transparent', color: p.hot ? '#000' : p.color, border: `1px solid ${p.color}` }}>
              {p.hot ? 'اشترك الآن ←' : 'اختر الخطة'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminPage({ token, accent, bg, card, text, muted, border, T, showToast }) {
  const [tab, setTab] = React.useState('stats');
  const [users, setUsers] = React.useState([]);
  const [stats, setStats] = React.useState({});
  const [settings, setSettings] = React.useState({});
  const [tickets, setTickets] = React.useState([]);
  const [signals, setSignals] = React.useState([]);
  const [replyText, setReplyText] = React.useState({});
  const [selectedUser, setSelectedUser] = React.useState(null);

  React.useEffect(() => {
    api('/admin/users', 'GET', null, token).then(r => { if (Array.isArray(r)) setUsers(r); });
    api('/admin/stats', 'GET', null, token).then(r => { if (r && !r.error) setStats(r); });
    api('/settings', 'GET', null, token).then(r => { if (r && !r.error) setSettings(r); });
    api('/tickets', 'GET', null, token).then(r => { if (Array.isArray(r)) setTickets(r); });
    api('/signals', 'GET', null, token).then(r => { if (Array.isArray(r)) setSignals(r); });
  }, [token]);

  const updatePlan = async (userId, plan) => {
    await api(`/admin/user/${userId}/plan`, 'PUT', { plan }, token);
    setUsers(u => u.map(x => x.id === userId ? { ...x, plan } : x));
    showToast('✅ تم تحديث الخطة');
  };

  const toggleUser = async (userId, isActive) => {
    await api(`/admin/user/${userId}/toggle`, 'PUT', {}, token);
    setUsers(u => u.map(x => x.id === userId ? { ...x, is_active: !isActive } : x));
    showToast(isActive ? '🔴 تم تعطيل الحساب' : '🟢 تم تفعيل الحساب');
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) return;
    await api(`/admin/user/${userId}/delete`, 'DELETE', null, token);
    setUsers(u => u.filter(x => x.id !== userId));
    showToast('🗑️ تم الحذف');
  };

  const saveSettings = async () => {
    await api('/settings', 'POST', settings, token);
    showToast('✅ تم حفظ الإعدادات');
  };

  const replyTicket = async (tid) => {
    if (!replyText[tid]) return;
    const r = await api(`/tickets/${tid}/reply`, 'POST', { message: replyText[tid] }, token);
    if (r && !r.error) {
      await api(`/tickets/${tid}/close`, 'POST', {}, token);
      setTickets(tk => tk.map(x => x.id === tid ? { ...x, status: 'closed' } : x));
      setReplyText(rt => ({ ...rt, [tid]: '' }));
      showToast('✅ تم الرد وإغلاق التذكرة');
    }
  };

  const tabs = [
    ['📊', 'stats', 'الإحصائيات'],
    ['👥', 'users', 'المستخدمين'],
    ['📡', 'signals', 'الإشارات'],
    ['🎫', 'tickets', 'التذاكر'],
    ['⚙️', 'settings', 'الإعدادات'],
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 900, color: text, marginBottom: 20 }}>🛡️ {T.admin}</h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {tabs.map(([icon, val, lbl]) => (
          <button key={val} onClick={() => setTab(val)}
            style={{ padding: '8px 18px', borderRadius: 20, border: '1.5px solid '+(tab===val ? accent : border),
              background: tab===val ? accent : 'transparent', color: tab===val ? '#fff' : text,
              cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            {icon} {lbl}
          </button>
        ))}
      </div>

      {/* Stats Tab */}
      {tab === 'stats' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 16, marginBottom: 24 }}>
            {[['👥 إجمالي المستخدمين', stats.total_users||0],
              ['🟢 النشطون', stats.active_users||0],
              ['🆓 المجانيون', stats.free_users||0],
              ['💎 Pro', stats.pro_users||0],
              ['📊 الصفقات', stats.total_trades||0],
              ['🎫 التذاكر المفتوحة', tickets.filter(t=>t.status==='open').length],
            ].map(([l,v]) => (
              <div key={l} className="stat-card">
                <div style={{ fontSize: 12, color: muted, marginBottom: 6 }}>{l}</div>
                <div style={{ fontSize: 24, fontWeight: 900, color: accent }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Users Tab */}
      {tab === 'users' && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid '+border }}>
                {['#','الاسم','البريد','الخطة','الحالة','تاريخ التسجيل','إجراءات'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', color: muted, fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid '+border }}>
                  <td style={{ padding: '10px 12px', textAlign: 'center', color: muted }}>{u.id}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 700, color: text }}>{u.name}</td>
                  <td style={{ padding: '10px 12px', color: muted, direction: 'ltr' }}>{u.email}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <select value={u.plan} onChange={e => updatePlan(u.id, e.target.value)}
                      style={{ background: bg, color: text, border: '1px solid '+border, borderRadius: 6, padding: '3px 8px', fontSize: 12 }}>
                      <option value="free">مجاني</option>
                      <option value="pro">Pro</option>
                      <option value="elite">Elite</option>
                    </select>
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <span style={{ background: u.is_active ? '#10b98122' : '#ef444422',
                      color: u.is_active ? '#10b981' : '#ef4444', borderRadius: 100, padding: '2px 10px', fontSize: 12 }}>
                      {u.is_active ? '🟢 نشط' : '🔴 موقوف'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', color: muted, fontSize: 11 }}>{u.created_at ? u.created_at.slice(0,10) : '-'}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                      <button onClick={() => toggleUser(u.id, u.is_active)}
                        style={{ background: u.is_active ? '#ef444422' : '#10b98122', color: u.is_active ? '#ef4444' : '#10b981',
                          border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
                        {u.is_active ? 'تعطيل' : 'تفعيل'}
                      </button>
                    <button onClick={async () => {
                      const np = window.prompt('كلمة السر الجديدة لـ ' + u.name + ':');
                      if (!np || np.length < 6) return showToast('كلمة السر قصيرة جداً', 'error');
                      const r = await api('/admin/user/' + u.id + '/reset-password', 'PUT', { new_password: np }, token);
                      if (r && r.ok) showToast('✅ تم تغيير كلمة السر');
                      else showToast(r?.error || 'خطأ', 'error');
                    }} style={{ background: '#3b82f622', color: '#3b82f6', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
                      🔑 كلمة السر
                    </button>
                      <button onClick={() => deleteUser(u.id)}
                        style={{ background: '#ef444422', color: '#ef4444', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
                        حذف
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Signals Tab */}
      {tab === 'signals' && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <h3 style={{ color: text, marginBottom: 16, fontSize: 15 }}>📡 الإشارات النشطة</h3>
          {signals.length === 0 && <div style={{ color: muted, textAlign: 'center', padding: 30 }}>لا توجد إشارات</div>}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid '+border }}>
                {['السهم','إشارة','السعر','الهدف','وقف','المتوسط','الثقة'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', color: muted, fontWeight: 700, textAlign: 'center' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {signals.map((s,i) => (
                <tr key={i} style={{ borderBottom: '1px solid '+border }}>
                  <td style={{ padding: '10px 12px', fontWeight: 900, color: text, textAlign: 'center' }}>{s.symbol}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <span style={{ background: s.action==='BUY'?'#10b98122':'#ef444422', color: s.action==='BUY'?'#10b981':'#ef4444', borderRadius: 100, padding: '2px 10px', fontSize: 12 }}>
                      {s.action==='BUY'?'🟢 شراء':'🔴 بيع'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', color: text }}>${s.price}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', color: '#10b981' }}>${s.tp}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', color: '#ef4444' }}>${s.sl}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', color: muted }}>{s.ma}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', color: accent, fontWeight: 700 }}>{s.confidence}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tickets Tab */}
      {tab === 'tickets' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {tickets.length === 0 && <div className="card" style={{ color: muted, textAlign: 'center', padding: 30 }}>لا توجد تذاكر</div>}
          {tickets.map(t => (
            <div key={t.id} className="card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div>
                  <span style={{ fontWeight: 700, color: text, fontSize: 15 }}>{t.subject}</span>
                  <span style={{ color: muted, fontSize: 12, marginRight: 10 }}>بواسطة: {t.user_name}</span>
                </div>
                <span style={{ background: t.status==='open'?'#10b98122':'#64748b22', color: t.status==='open'?'#10b981':muted, borderRadius: 100, padding: '2px 10px', fontSize: 12 }}>
                  {t.status==='open'?'🟢 مفتوحة':'✅ مغلقة'}
                </span>
              </div>
              <p style={{ color: muted, fontSize: 13, marginBottom: 12 }}>{t.message}</p>
              {t.replies && t.replies.map((r,i) => (
                <div key={i} style={{ background: r.is_admin?'#10b98122':bg, borderRadius: 8, padding: '8px 12px', marginBottom: 8, borderRight: r.is_admin?'3px solid #10b981':'none' }}>
                  <div style={{ fontSize: 11, color: r.is_admin?'#10b981':muted, marginBottom: 4 }}>{r.is_admin?'👨‍💼 رد الإدارة':'👤 المستخدم'}</div>
                  <div style={{ fontSize: 13, color: text }}>{r.message}</div>
                </div>
              ))}
              {t.status === 'open' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input className="inp" placeholder="اكتب ردك..." value={replyText[t.id]||''}
                    onChange={e => setReplyText(r => ({...r,[t.id]:e.target.value}))}
                    style={{ flex: 1, fontSize: 13 }} />
                  <button className="btn btn-gold" style={{ padding: '8px 16px', fontSize: 13 }}
                    onClick={() => replyTicket(t.id)}>رد وأغلق</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Settings Tab */}
      {tab === 'settings' && (
        <div className="card" style={{ maxWidth: 600 }}>
          <h3 style={{ color: text, marginBottom: 16, fontSize: 15 }}>⚙️ إعدادات الموقع</h3>
          {[['announcement','شريط الإعلان'],['ticker','نص الإعلان المتحرك'],['twitter_url','رابط X (تويتر)'],['facebook_url','رابط Facebook'],['disclaimer','نص إقرار المسؤولية']].map(([k,l]) => (
            <div key={k} style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: muted, display: 'block', marginBottom: 4 }}>{l}</label>
              {k==='disclaimer' ? 
                <textarea className="inp" value={settings[k]||''} onChange={e => setSettings(s=>({...s,[k]:e.target.value}))} rows={4} style={{ resize: 'vertical' }} /> :
                <input className="inp" value={settings[k]||''} onChange={e => setSettings(s=>({...s,[k]:e.target.value}))} />
              }
            </div>
          ))}
          <button className="btn btn-gold" onClick={saveSettings}>💾 حفظ الإعدادات</button>
        </div>
      )}
    </div>
  );
}

function WeinsteinPage({ accent, bg, card, text, muted, border, T, token }) {
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [filter, setFilter] = React.useState('all');
  const [scanned, setScanned] = React.useState(false);

  const scan = async (f) => {
    const activeFilter = f !== undefined ? f : filter;
    setLoading(true);
    setFilter(activeFilter);
    try {
      const r = await api(`/weinstein/scan?stage=${activeFilter}`, 'GET', null, token);
      setResults(Array.isArray(r) ? r : []);
      setScanned(true);
    } catch(e) {}
    setLoading(false);
  };

  const stageColors = { 1: '#f59e0b', 2: '#10b981', 3: '#f59e0b', 4: '#ef4444' };
  const stageNames = { 1: '① تراكم', 2: '② صعود', 3: '③ توزيع', 4: '④ هبوط' };
  const stages = [
    {
      num: '①',
      name: T.lang === 'ar' ? 'تراكم (Accumulation)' : 'Stage 1 — Accumulation',
      color: '#f59e0b',
      points: T.lang === 'ar'
        ? ['السهم يتحرك أفقياً بعد هبوط', 'المتوسطات مسطحة']
        : ['Stock moves sideways after decline', 'Moving averages flatten'],
    },
    {
      num: '②',
      name: T.lang === 'ar' ? 'صعود (Advancing)' : 'Stage 2 — Advancing',
      color: '#10b981',
      highlight: true,
      points: T.lang === 'ar'
        ? ['← هذه المرحلة المثالية للشراء', 'السعر فوق MA 30 الأسبوعي', 'المتوسطات صاعدة']
        : ['← Ideal buying stage', 'Price above 30-week MA', 'Moving averages rising'],
    },
    {
      num: '③',
      name: T.lang === 'ar' ? 'توزيع (Distribution)' : 'Stage 3 — Distribution',
      color: '#f59e0b',
      points: T.lang === 'ar'
        ? ['السهم يتحرك أفقياً بعد صعود', 'المتوسطات تبدأ تتسطح']
        : ['Stock moves sideways after rally', 'Moving averages start to flatten'],
    },
    {
      num: '④',
      name: T.lang === 'ar' ? 'هبوط (Declining)' : 'Stage 4 — Declining',
      color: '#ef4444',
      points: T.lang === 'ar'
        ? ['السعر تحت MA 30', 'المتوسطات هابطة']
        : ['Price below 30-week MA', 'Moving averages declining'],
    },
  ];
  return (
    <div style={{ padding: 24, direction: T.dir }}>
      <h2 style={{ color: text, marginBottom: 4, fontSize: 20 }}>📊 فلتر وينشتاين</h2>
      <p style={{ color: muted, marginBottom: 16, fontSize: 13 }}>اكتشف الأسهم الجاهزة للدخول حسب مراحل وينشتاين</p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {[['all','الكل'],['1','① تراكم'],['2','② صعود ✅'],['3','③ توزيع'],['4','④ هبوط 🔴'],['rs','Stage2+RS80']].map(([v,l]) => (
          <button key={v} className="btn" onClick={() => { setFilter(v); scan(v); }}
            style={{ background: filter===v ? accent : card, color: filter===v ? '#fff' : text, border: `1px solid ${border}`, padding: '6px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
            {l}
          </button>
        ))}
        <button className="btn btn-gold" onClick={() => scan(filter)} style={{ padding: '6px 16px', fontSize: 13 }}>
          {loading ? '⏳ جاري الفحص...' : '🔍 فحص الأسهم'}
        </button>
      </div>

      {!scanned && !loading && (
        <div style={{ textAlign: 'center', padding: 60, color: muted }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
          <div>اضغط "فحص الأسهم" لتحليل السوق</div>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 60, color: muted }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
          <div>جاري تحليل الأسهم... قد يستغرق دقيقة</div>
        </div>
      )}

      {!loading && scanned && (
        <>
          <div style={{ marginBottom: 12, color: muted, fontSize: 13 }}>
            تم العثور على {results.length} سهم
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {results.map(r => (
              <div key={r.symbol} style={{ background: card, border: `1px solid ${r.near_breakout ? '#10b981' : border}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: 16, color: text, minWidth: 60 }}>{r.symbol}</span>
                <span style={{ background: r.color + '22', color: r.color, borderRadius: 100, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                  {stageNames[r.stage]}
                </span>
                {r.near_breakout && <span style={{ background: '#10b98122', color: '#10b981', borderRadius: 100, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>🚀 قريب من الاختراق</span>}
                <span style={{ color: muted, fontSize: 13 }}>السعر: ${r.price}</span>
                <span style={{ color: muted, fontSize: 13 }}>MA30: ${r.ma30}</span>
                <span style={{ color: r.ma30_slope > 0 ? '#10b981' : '#ef4444', fontSize: 13 }}>
                  ميل MA: {r.ma30_slope > 0 ? '↑' : '↓'} {Math.abs(r.ma30_slope)}%
                </span>
                <span style={{ color: muted, fontSize: 13 }}>حجم: {r.vol_ratio}x</span>
                <span style={{ background: r.rs >= 80 ? '#10b98122' : 'transparent', color: r.rs >= 80 ? '#10b981' : muted, borderRadius: 6, padding: '1px 6px', fontSize: 13, fontWeight: r.rs >= 80 ? 700 : 400 }}>RS: {r.rs}</span>
              </div>
            ))}
          </div>
          {results.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: muted }}>لا توجد أسهم في هذه المرحلة حالياً</div>
          )}
        </>
      )}

      <div style={{ marginTop: 24, background: '#10b98122', border: '2px solid #10b981', borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#10b981' }}>
          💪 Stage 2 + حجم متزايد = أقوى إشارة شراء
        </div>
      </div>
      <div style={{ marginTop: 16, background: '#ef444422', border: '2px solid #ef4444', borderRadius: 12, padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#ef4444' }}>🔴 تنبيه Stage 4 - شورت</div>
          <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>يفحص الأسهم ويرسل تنبيه تلغرام + يحفظ في الإشارات</div>
        </div>
        <button className="btn" onClick={async () => {
          try {
            const r = await api('/weinstein/alert_stage4', 'POST', {}, token);
            alert(r.sent > 0 ? `✅ تم إرسال تنبيه لـ ${r.sent} سهم: ${r.symbols.join(', ')}` : '✅ لا توجد أسهم في Stage 4 حالياً');
          } catch(e) { alert('خطأ في الاتصال'); }
        }} style={{ background: '#ef4444', color: '#fff', padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700 }}>
          🔍 فحص Stage 4 الآن
        </button>
      </div>
    </div>
  );
}

function OptionsSignalsPage({ accent, bg, card, text, muted, border, T, token }) {
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [onlyConfirmed, setOnlyConfirmed] = React.useState(true);
  const [scanned, setScanned] = React.useState(false);
  const [error, setError] = React.useState('');

  const scan = async () => {
    setLoading(true);
    setError('');
    try {
      // نستخدم fetch مباشرة مع timeout طويل لأن الفحص يأخذ وقتاً
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000);
      const resp = await fetch(`/api/options/signals?confirmed=${onlyConfirmed}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      const r = await resp.json();
      if (r && r.error) {
        setError(r.error);
        setResults([]);
      } else {
        setResults(Array.isArray(r) ? r : []);
      }
      setScanned(true);
    } catch(e) {
      setError('خطأ في الاتصال');
    }
    setLoading(false);
  };

  return (
    <div style={{ padding: 24, direction: T.dir }}>
      <h2 style={{ color: text, marginBottom: 4, fontSize: 20 }}>🎯 إشارات الأوبشن</h2>
      <p style={{ color: muted, marginBottom: 16, fontSize: 13 }}>
        Call/Put بناءً على مرحلة وينشتاين + تأكيد Open Interest الشهري
      </p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <button className="btn" onClick={() => { setOnlyConfirmed(true); }}
          style={{ background: onlyConfirmed ? accent : card, color: onlyConfirmed ? '#fff' : text, border: `1px solid ${border}`, padding: '6px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
          ✅ المؤكدة فقط
        </button>
        <button className="btn" onClick={() => { setOnlyConfirmed(false); }}
          style={{ background: !onlyConfirmed ? accent : card, color: !onlyConfirmed ? '#fff' : text, border: `1px solid ${border}`, padding: '6px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
          عرض الكل
        </button>
        <button className="btn btn-gold" onClick={scan} style={{ padding: '6px 16px', fontSize: 13 }}>
          {loading ? '⏳ جاري الفحص... (قد يستغرق دقيقة)' : '🔍 فحص الإشارات'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#2d0a0a', border: '1px solid #ef4444', borderRadius: 8, padding: '10px 14px', color: '#ef4444', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {!scanned && !loading && !error && (
        <div style={{ textAlign: 'center', padding: 60, color: muted }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎯</div>
          <div>اضغط "فحص الإشارات" لتحليل السوق (يتطلب ربط Alpaca)</div>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 60, color: muted }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
          <div>جاري تحليل المراحل وبيانات الأوبشن... يستغرق دقيقة تقريباً</div>
        </div>
      )}

      {!loading && scanned && !error && (
        <>
          <div style={{ marginBottom: 12, color: muted, fontSize: 13 }}>
            تم العثور على {results.length} إشارة
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {results.map(r => (
              <div key={r.symbol} style={{ background: card, border: `2px solid ${r.confirmed ? (r.recommendation === 'Call' ? '#10b981' : '#ef4444') : border}`, borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: 16, color: text, minWidth: 60 }}>{r.symbol}</span>
                  <span style={{ fontSize: 20 }}>{r.rec_emoji}</span>
                  <span style={{ background: r.confirmed ? (r.recommendation === 'Call' ? '#10b98122' : '#ef444422') : '#f59e0b22', color: r.confirmed ? (r.recommendation === 'Call' ? '#10b981' : '#ef4444') : '#f59e0b', borderRadius: 100, padding: '3px 12px', fontSize: 13, fontWeight: 700 }}>
                    {r.recommendation}
                  </span>
                  <span style={{ color: muted, fontSize: 12 }}>السعر: ${r.price}</span>
                  {r.rs !== undefined && <span style={{ fontSize: 12, fontWeight: 700, color: r.rs >= 80 ? '#10b981' : r.rs >= 50 ? '#f59e0b' : '#ef4444' }}>RS: {r.rs}</span>}
                </div>
                {r.oi && (
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: muted, marginTop: 4 }}>
                    <span>تاريخ الانتهاء: {r.oi.expiry}</span>
                    <span style={{ color: '#10b981' }}>Call OI: {r.oi.call_oi.toLocaleString()} ({r.oi.call_pct}%)</span>
                    <span style={{ color: '#ef4444' }}>Put OI: {r.oi.put_oi.toLocaleString()} ({r.oi.put_pct}%)</span>
                    <span>إجمالي العقود: {r.oi.total_contracts}</span>
                  </div>
                )}
                {!r.oi && (
                  <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>⚠️ لا تتوفر بيانات OI لهذا السهم حالياً</div>
                )}
              </div>
            ))}
          </div>
          {results.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: muted }}>لا توجد إشارات مؤكدة حالياً - هذا طبيعي، الإشارات القوية نادرة</div>
          )}
        </>
      )}
    </div>
  );
}
